import {
  useRentalManagerUserPropertyExternalTourV2WebApiHandler_addExternalTourForListing,
  useRentalManagerUserPropertyExternalTourV2WebApiHandler_addExternalTourForModel,
  useRentalManagerUserPropertyExternalTourV2WebApiHandler_removeExternalTourFromListing,
  useRentalManagerUserPropertyExternalTourV2WebApiHandler_removeExternalTourFromModel,
  useRentalManagerUserPropertyVrModelV2WebApiHandler_associateListing,
  useRentalManagerUserPropertyVrModelV2WebApiHandler_associateModel,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { ErrorMessageConstants } from '@zg-rentals/zillow-rental-manager/constants';

import { mapTourTypeDisplayToEnum } from '../utils/tourTypeMapping';

import { usePropertyEditMutation } from './usePropertyEditMutation';

import type {
  GetUserPropertyDetailsFloorPlanUnitWebDto,
  GetUserPropertyDetailsFloorPlanWebDto,
  GetUserPropertyDetailsResponseWebDto,
  RentalManagerExternalTourDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type AssociateParams = {
  shouldAssociate?: boolean;
  tourId?: string;
  vrModelReferenceId?: string;
  externalTour?: RentalManagerExternalTourDto;
};

type UseTourAssociationOptions = {
  listingAlias: string;
  listingDetails?: GetUserPropertyDetailsResponseWebDto;
  floorPlanIndex?: number | null;
  unitIndex?: number | null;
};

export const useTourAssociation = ({
  listingAlias,
  listingDetails,
  floorPlanIndex = null,
  unitIndex = null,
}: UseTourAssociationOptions) => {
  const { mutateAsync: associateVrModel } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyVrModelV2WebApiHandler_associateModel,
    successToastMessage: 'Tour updated',
    meta: {
      context: {
        api: 'rental-manager-user-property-vr-model',
        details: 'associate VR model',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: associateVrModelListing } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyVrModelV2WebApiHandler_associateListing,
    successToastMessage: 'Tour updated',
    meta: {
      context: {
        api: 'rental-manager-user-property-vr-model',
        details: 'associate VR model to listing',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: addExternalTourForListing } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyExternalTourV2WebApiHandler_addExternalTourForListing,
    successToastMessage: 'Tour added',
    meta: {
      context: {
        api: 'rental-manager-user-property-external-tour',
        details: 'add external tour for listing',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: removeExternalTourFromListing } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyExternalTourV2WebApiHandler_removeExternalTourFromListing,
    successToastMessage: 'Tour removed',
    meta: {
      context: {
        api: 'rental-manager-user-property-external-tour',
        details: 'remove external tour from listing',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: addExternalTourForModel } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyExternalTourV2WebApiHandler_addExternalTourForModel,
    successToastMessage: 'Tour added',
    meta: {
      context: {
        api: 'rental-manager-user-property-external-tour',
        details: 'add external tour for model',
      },
      invalidates: ['tours'],
    },
  });

  const { mutateAsync: removeExternalTourFromModel } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyExternalTourV2WebApiHandler_removeExternalTourFromModel,
    successToastMessage: 'Tour removed',
    meta: {
      context: {
        api: 'rental-manager-user-property-external-tour',
        details: 'remove external tour from model',
      },
      invalidates: ['tours'],
    },
  });

  const getModelId = ({
    floorPlanIndex = null,
    unitIndex = null,
    currentProperty: { floorplans, units },
  }: {
    floorPlanIndex?: number | null;
    unitIndex?: number | null;
    currentProperty: {
      floorplans: Array<GetUserPropertyDetailsFloorPlanWebDto>;
      units: Array<GetUserPropertyDetailsFloorPlanUnitWebDto>;
    };
  }): string | null => {
    const floorPlan = floorPlanIndex !== null && floorplans[floorPlanIndex];
    // Unit tour without floor plan
    if (floorPlanIndex === null && unitIndex !== null) {
      return units[unitIndex].id;
    }

    // Unit tour with floor plan
    if (floorPlanIndex !== null && unitIndex !== null) {
      const unit = floorPlan && floorPlan?.units?.[unitIndex];
      return unit ? unit.id : null;
    }
    return floorPlan ? floorPlan.id : null;
  };

  const handleAssociate = ({ shouldAssociate = false, tourId, vrModelReferenceId, externalTour }: AssociateParams) => {
    const isFloorPlanOrUnit3dTour = floorPlanIndex !== null || unitIndex !== null;

    const modelId =
      isFloorPlanOrUnit3dTour &&
      listingDetails &&
      getModelId({
        floorPlanIndex,
        currentProperty: { floorplans: listingDetails?.floorPlans || [], units: listingDetails?.units || [] },
        unitIndex,
      });

    // Building-level tours (2-part referenceId like "Appfolio/buildingId") are stored in BE with
    // feedModelId=NO_MODEL_ID regardless of floorPlan/unit context (e.g. label-matched by useGetTours).
    const isBuildingLevel = (referenceId?: string) => (referenceId ? referenceId.split('/').length <= 2 : false);

    if (tourId) {
      if (isFloorPlanOrUnit3dTour && !isBuildingLevel(vrModelReferenceId)) {
        return associateVrModel({
          postData: {
            vrModelId: Number(tourId),
            listingAlias,
            shouldAssociate: !!shouldAssociate,
            encodedFloorplanKey: (modelId as string) ?? '',
          },
        });
      }
      return associateVrModelListing({
        postData: { vrModelId: Number(tourId), listingAlias, shouldAssociate: !!shouldAssociate },
      });
    }

    if (externalTour) {
      const tourData = {
        label: externalTour.label,
        tourUrl: externalTour.tourUrl,
        sourceName: externalTour.sourceName,
        tourType: mapTourTypeDisplayToEnum(externalTour.tourType),
        published: true,
      };

      if (isFloorPlanOrUnit3dTour && !isBuildingLevel(externalTour.referenceId)) {
        const modelTourData = {
          ...tourData,
          listingAlias,
          encodedFloorplanKey: (modelId as string) ?? '',
        };

        if (shouldAssociate) {
          return addExternalTourForModel({ postData: modelTourData });
        } else {
          return removeExternalTourFromModel({ postData: modelTourData });
        }
      } else {
        if (shouldAssociate) {
          return addExternalTourForListing({ postData: { ...tourData, listingAlias } });
        } else {
          return removeExternalTourFromListing({ postData: { ...tourData, listingAlias } });
        }
      }
    }

    return Promise.resolve();
  };

  const handleTourError = (
    error: unknown,
    isMultifamily?: boolean,
    isMultifamilyCustomer?: boolean,
    isSingleFamilyEnterpriseCustomer?: boolean,
  ) => {
    const errorMessage = DatarouterError.getDisplayMessage(error);
    const lowerCaseMessage = errorMessage.toLowerCase();

    if (lowerCaseMessage.includes('invalid url')) {
      return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_INVALID_URL);
    }
    if (lowerCaseMessage.includes('tour already exists')) {
      return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_ALREADY_EXIST);
    }
    if (lowerCaseMessage.includes('is not from a supported provider')) {
      return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_NOT_SUPPORTED_PROVIDER);
    }
    if (lowerCaseMessage.includes('doesn’t have permission')) {
      return Promise.reject(
        ErrorMessageConstants.EXTERNAL_TOUR_NO_PERMISSION(
          isMultifamily && isMultifamilyCustomer,
          isSingleFamilyEnterpriseCustomer,
        ),
      );
    }
    return Promise.reject('Cannot associate this tour, refresh the page and try again');
  };

  return {
    handleAssociate,
    handleTourError,
  };
};
